import { chainUrl } from '../../config'
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults'
import { publicKey, unwrapOption } from '@metaplex-foundation/umi'
import { mplCore } from '@metaplex-foundation/mpl-core'
import { fetchCandyGuard, fetchCandyMachine, mplCandyMachine } from '@metaplex-foundation/mpl-core-candy-machine'

const ORCHESTRATOR = '/chain-builder/orchestrator'
const CACHE_MS = 60_000
const cache = new Map()

export function browserRpcProxyUrl() {
  const value = chainUrl(`${ORCHESTRATOR}/rpc`)
  if (/^https?:\/\//i.test(value)) return value
  if (typeof window !== 'undefined') return new URL(value, window.location.origin).toString()
  return value
}

function guardLamports(amount) {
  if (!amount) return null
  const basisPoints = amount?.basisPoints
  if (basisPoints === undefined || basisPoints === null) return null
  const value = Number(basisPoints)
  return Number.isFinite(value) && value >= 0 ? value : null
}

function optionValue(option) {
  try {
    return option?.__option ? unwrapOption(option) : null
  } catch {
    return null
  }
}

export async function readOnChainMintPricing(candyMachineAddress) {
  const address = String(candyMachineAddress || '').trim()
  if (!address) return null
  const hit = cache.get(address)
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.value

  const umi = createUmi(browserRpcProxyUrl()).use(mplCore()).use(mplCandyMachine())
  const candyMachine = await fetchCandyMachine(umi, publicKey(address))
  const candyGuard = await fetchCandyGuard(umi, publicKey(candyMachine.mintAuthority))
  const solPayment = optionValue(candyGuard?.guards?.solPayment)
  const solFixedFee = optionValue(candyGuard?.guards?.solFixedFee)
  const value = {
    creatorPriceLamports: guardLamports(solPayment?.lamports),
    roseFeeLamports: guardLamports(solFixedFee?.lamports),
    treasury: solPayment?.destination ? String(solPayment.destination) : null,
    roseTreasury: solFixedFee?.destination ? String(solFixedFee.destination) : null,
    source: 'candy-guard'
  }
  cache.set(address, { at: Date.now(), value })
  return value
}

export async function withOnChainLaunchPricing(launch) {
  if (!launch?.candy_machine_address && !launch?.candyMachineAddress) return launch
  try {
    const pricing = await readOnChainMintPricing(launch.candy_machine_address || launch.candyMachineAddress)
    if (!pricing) return launch
    return {
      ...launch,
      mint_price_lamports: pricing.creatorPriceLamports ?? Number(launch.mint_price_lamports || launch.price_lamports || 0),
      rose_fee_lamports: pricing.roseFeeLamports ?? Number(launch.rose_fee_lamports || launch.per_mint_fee_lamports || 0),
      pricing_source: pricing.source
    }
  } catch {
    return launch
  }
}
